import { externalRoutesPrefixList } from '../constants/externalRoutesConstants';

let listenerRegistered = false;

// Resolved once at module load; CRA inlines env at build time.
// Falls back to '*' only if the env is missing/malformed at build time —
// in production REACT_APP_PORTAL_MAIN_URL is expected to be set.
const PARENT_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_PORTAL_MAIN_URL || '').origin;
  } catch {
    return '*';
  }
})();

// react-router-dom Link prepends the BrowserRouter basename to anchor.href.
// Parent expects basename-relative paths in route messages (otherwise the
// iframe re-applies basename on the next parent-to-iframe sync, producing
// a doubled prefix like /staticky-obsah-fe/staticky-obsah-fe/aktuality and a
// NotFoundPage). Strip the basename once here.
const BASENAME = (process.env.REACT_APP_SUBPATH || '').replace(/\/+$/, '');

const stripBasename = (pathname: string): string => {
  if (!BASENAME) return pathname;
  if (pathname === BASENAME) return '/';
  if (pathname.startsWith(`${BASENAME}/`)) return pathname.slice(BASENAME.length);
  return pathname;
};

const interceptClickEvent = (event: MouseEvent): void => {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

  // event.target may be a text node or non-HTML element (e.g. SVG inside <a>);
  // narrow to Element before walking the DOM.
  const { target } = event;
  if (!(target instanceof Element)) return;

  const anchor = target.closest('a');
  if (!(anchor instanceof HTMLAnchorElement)) return;

  // Only intercept links that would navigate the current frame.
  const linkTarget = anchor.getAttribute('target');
  if (linkTarget && linkTarget.toLowerCase() !== '_self') return;

  // Native download flow — let the browser handle it.
  if (anchor.hasAttribute('download')) return;

  const href = anchor.getAttribute('href');
  if (!href) return;
  if (href.startsWith('#')) return;

  // SLASLX-3400 (S): raw <a href> in CMS-rendered HTML otherwise triggers a
  // full-document iframe navigation, which appends an extra entry to the joint
  // session history that the portal's replaceState dedup can't fold — forcing
  // the user to press Back twice to leave. Classify by the resolved URL so all
  // href shapes (absolute, root-relative, relative, ./, ../) are caught.
  let resolved: URL;
  try {
    resolved = new URL(anchor.href);
  } catch {
    return;
  }

  // resolved.protocol is normalized to lowercase by the URL API, so a single
  // comparison catches MAILTO:, JavaScript:, etc.
  if (
    // eslint-disable-next-line no-script-url
    resolved.protocol === 'javascript:'
    || resolved.protocol === 'mailto:'
    || resolved.protocol === 'tel:'
  ) return;

  if (resolved.origin !== window.location.origin) {
    event.preventDefault();
    window.parent.postMessage({
      type: 'iframe-to-parent-absolute-url',
      route: anchor.href,
    }, PARENT_ORIGIN);
    return;
  }

  event.preventDefault();
  const path = stripBasename(resolved.pathname) + resolved.search + resolved.hash;
  const isExternal = externalRoutesPrefixList.some(prefix => path.startsWith(prefix));
  window.parent.postMessage({
    type: isExternal ? 'iframe-to-parent-external-route' : 'iframe-to-parent-internal-route',
    route: path,
  }, PARENT_ORIGIN);
};

const handleAbsoluteUrlRedirects = (): void => {
  if (listenerRegistered) return;
  if (!document.addEventListener) return;
  // SLASLX-3400 cross-microfrontend 2× Back: capture phase runs BEFORE
  // react-router-dom Link's onClick (bubble). preventDefault here makes
  // react-router-dom skip its internal navigate (it checks defaultPrevented),
  // so the iframe doesn't push a history entry — only the parent does.
  // Result: 1 history entry per user click, 1 Back to return.
  document.addEventListener('click', interceptClickEvent, true);
  listenerRegistered = true;
};

export default handleAbsoluteUrlRedirects;
